// Modal "Enviar": escolher pessoas (ou digitar e-mails) → Enviar (PDF + planilha).
// Ou mandar o link pelo WhatsApp / copiar. Acesso com login fica numa tela separada.
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import type { Permission, ShareInfo, Suggestion } from '../lib/types';
import { Avatar, Button, Loading, Sheet } from './ui';
import { useToast } from './feedback';

export type ShareTarget = { type: 'restaurant' | 'visit_report' | 'period_report'; id: string };

const PERM: Array<[Permission, string]> = [['viewer', 'Leitor'], ['commenter', 'Comentador'], ['editor', 'Editor']];
const PERM_HINT: Record<Permission, string> = {
  viewer: 'Só vê os relatórios.',
  commenter: 'Vê, comenta e responde às pendências com plano de ação.',
  editor: 'Também pode compartilhar (só para nutricionistas).',
};
const validEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
const splitEmails = (s: string) => s.split(/[\s,;]+/).map((x) => x.trim().toLowerCase()).filter(Boolean);

// ───────── Campo "Para quem?" com sugestões ─────────
function RecipientPicker({ value, onChange, suggestions, placeholder }: {
  value: string[]; onChange: (v: string[]) => void; suggestions: Suggestion[]; placeholder?: string;
}) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [focus, setFocus] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const q = text.trim().toLowerCase();
  const available = suggestions.filter((s) => !value.includes(s.email));
  const matches = q ? available.filter((s) => s.email.includes(q) || s.nome.toLowerCase().includes(q)).slice(0, 6) : [];
  const nameOf = (email: string) => suggestions.find((s) => s.email === email)?.nome;

  const add = (emails: string[]) => {
    const bad = emails.filter((e) => !validEmail(e));
    if (bad.length) { toast(`E-mail inválido: ${bad[0]}`, 'err'); return false; }
    onChange([...new Set([...value, ...emails])]);
    setText('');
    return true;
  };
  const commit = () => { const list = splitEmails(text); if (list.length) add(list); };

  return (
    <div>
      <div
        className="row wrap gap-6"
        onClick={() => input.current?.focus()}
        style={{ minHeight: 48, padding: '6px 8px', border: `1px solid ${focus ? 'var(--focus)' : 'var(--border-input)'}`, borderRadius: 10, background: focus ? '#fff' : 'var(--input-bg)', cursor: 'text' }}
      >
        {value.map((e) => (
          <span key={e} className="row gap-6" style={{ maxWidth: '100%', padding: '5px 6px 5px 10px', borderRadius: 16, background: 'var(--accent-soft)', color: 'var(--resolved)', fontSize: 13 }}>
            <span className="wrapline">{nameOf(e) ? <><strong style={{ fontWeight: 500 }}>{nameOf(e)}</strong> <span style={{ opacity: 0.75 }}>{e}</span></> : e}</span>
            <button type="button" onClick={(ev) => { ev.stopPropagation(); onChange(value.filter((x) => x !== e)); }} aria-label={`Remover ${e}`} style={{ border: 0, background: 'none', color: 'inherit', fontSize: 15, padding: '0 4px', lineHeight: 1 }}>×</button>
          </span>
        ))}
        <input
          ref={input}
          type="text" inputMode="email" autoComplete="email" autoCapitalize="none"
          value={text}
          onChange={(e) => (/[,;\s]$/.test(e.target.value) && validEmail(e.target.value.trim().replace(/[,;]$/, '')) ? add([e.target.value.trim().replace(/[,;]$/, '').toLowerCase()]) : setText(e.target.value))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); if (matches[0] && !validEmail(q)) add([matches[0].email]); else commit(); }
            if (e.key === 'Backspace' && !text && value.length) onChange(value.slice(0, -1));
          }}
          onFocus={() => setFocus(true)}
          onBlur={() => { setFocus(false); if (validEmail(q)) commit(); }}
          placeholder={value.length ? 'Adicionar outro' : placeholder ?? 'Nome ou e-mail'}
          style={{ flex: '1 1 160px', minWidth: 120, border: 0, background: 'transparent', height: 34, padding: '0 4px' }}
        />
      </div>
      {matches.length > 0 && (
        <div style={{ marginTop: 4, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          {matches.map((s) => (
            <button key={s.email} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => add([s.email])} className="row gap-10" style={{ width: '100%', padding: '9px 10px', border: 0, borderBottom: '1px solid var(--divider-2)', background: '#fff', textAlign: 'left' }}>
              <Avatar name={s.nome} size={28} />
              <span className="flex1 wrapline"><span style={{ fontSize: 13.5, fontWeight: 500 }}>{s.nome}</span><br /><span className="small">{s.email} · {s.origem}</span></span>
            </button>
          ))}
        </div>
      )}
      {!q && available.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div className="small" style={{ marginBottom: 6 }}>Sugestões</div>
          <div className="row wrap gap-6">
            {available.slice(0, 6).map((s) => (
              <button key={s.email} type="button" className="chip" style={{ height: 'auto', minHeight: 36, padding: '6px 12px', textAlign: 'left', whiteSpace: 'normal' }} onClick={() => add([s.email])}>
                + <strong style={{ fontWeight: 500 }}>{s.nome}</strong> <span className="small">· {s.origem}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Divider({ children }: { children: ReactNode }) {
  return (
    <div className="row gap-10" style={{ margin: '18px 0 12px' }}>
      <div className="flex1" style={{ height: 1, background: 'var(--divider)' }} />
      <span className="small">{children}</span>
      <div className="flex1" style={{ height: 1, background: 'var(--divider)' }} />
    </div>
  );
}

const WaIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 18.2c-1.5 0-3-.4-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.2-.4.2-.4.7-1.3.1-.1 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2c0 1.3.9 2.5 1.1 2.7.1.2 1.8 2.8 4.4 3.9 1.6.7 2.3.8 3.1.6.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2-.1-.1-.2-.2-.4-.3Z" /></svg>
);

// ───────── WhatsApp / copiar link (link sem login, assinado, 90 dias) ─────────
function LinkButtons({ tid, target }: { tid: string; target: ShareTarget }) {
  const toast = useToast();
  const q = useQuery({
    queryKey: ['quick-link', tid, target.type, target.id],
    queryFn: () => api.get<{ url: string; label: string }>(`/api/t/${tid}/shares/quick-link?type=${target.type}&id=${encodeURIComponent(target.id)}`),
    staleTime: 10 * 60_000,
  });
  const url = q.data?.url ?? '';
  const label = q.data?.label ?? '';
  const msg = target.type === 'restaurant'
    ? `Olá! Por este link você acompanha as visitas técnicas e as pendências de ${label}:\n${url}`
    : `Olá! Segue o ${label.charAt(0).toLowerCase()}${label.slice(1)}:\n${url}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast('Link copiado'); } catch { window.prompt('Copie o link:', url); }
  };
  const btn = { height: 50, borderRadius: 11, fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, textDecoration: 'none', width: '100%' } as const;
  if (!q.data) return <Loading rows={1} height={50} />;
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: typeof navigator.share === 'function' ? '1fr 1fr 1fr' : '1fr 1fr', gap: 8 }}>
        <a href={`https://wa.me/?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer" style={{ ...btn, background: '#25D366', color: '#fff' }}><WaIcon /> WhatsApp</a>
        <button type="button" onClick={copy} style={{ ...btn, background: '#fff', border: '1px solid var(--border-input)', color: 'var(--text)' }}>Copiar link</button>
        {typeof navigator.share === 'function' && (
          <button type="button" onClick={() => navigator.share({ title: label, text: msg }).catch(() => undefined)} style={{ ...btn, background: '#fff', border: '1px solid var(--border-input)', color: 'var(--text)' }}>Mais…</button>
        )}
      </div>
      <div className="small" style={{ marginTop: 8, lineHeight: 1.45 }}>O link abre sem cadastro, só para ver. Vale por 90 dias.</div>
    </>
  );
}

// ───────── Relatório: escolher pessoas → enviar com PDF + planilha ─────────
function SendReport({ tid, target, suggestions }: { tid: string; target: ShareTarget; suggestions: Suggestion[] }) {
  const toast = useToast();
  const [to, setTo] = useState<string[]>([]);
  const send = useMutation({
    mutationFn: () => {
      if (target.type === 'visit_report') return api.post(`/api/t/${tid}/visits/${target.id}/send`, { emails: to, format: 'ambos' });
      const [kind, inicio] = target.id.split(':');
      return api.post(`/api/t/${tid}/reports/period/send`, { emails: to, format: 'ambos', kind, inicio });
    },
    onSuccess: () => { toast(to.length === 1 ? `Enviado para ${to[0]}` : `Enviado para ${to.length} pessoas`); setTo([]); },
    onError: (e) => toast(errMsg(e), 'err'),
  });
  return (
    <>
      <div className="label" style={{ marginBottom: 6 }}>Para quem?</div>
      <RecipientPicker value={to} onChange={setTo} suggestions={suggestions} placeholder="Nome ou e-mail (pode ser mais de um)" />
      <Button variant="dark" size="lg" block loading={send.isPending} disabled={!to.length} style={{ marginTop: 12 }} onClick={() => send.mutate()}>
        {to.length > 1 ? `Enviar para ${to.length} pessoas` : 'Enviar por e-mail'}
      </Button>
      <div className="small" style={{ textAlign: 'center', marginTop: 6 }}>Vai com o PDF e a planilha anexados.</div>
    </>
  );
}

// ───────── Restaurante: quem recebe sozinho a cada visita ─────────
function AutoRecipients({ tid, rid, suggestions }: { tid: string; rid: string; suggestions: Suggestion[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const key = ['recipients', tid, rid];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<Array<{ email: string; format: string }>>(`/api/t/${tid}/restaurants/${rid}/recipients`) });
  const save = useMutation({
    mutationFn: (emails: string[]) => api.put(`/api/t/${tid}/restaurants/${rid}/recipients`, { recipients: emails.map((email) => ({ email, format: 'ambos' })) }),
    onMutate: (emails) => qc.setQueryData(key, emails.map((email) => ({ email, format: 'ambos' }))),
    onSuccess: () => toast('Salvo'),
    onError: (e) => { toast(errMsg(e), 'err'); qc.invalidateQueries({ queryKey: key }); },
  });
  return (
    <>
      <div className="label" style={{ marginBottom: 2 }}>Quem recebe os relatórios desta unidade</div>
      <div className="small" style={{ marginBottom: 8, lineHeight: 1.45 }}>A cada visita finalizada, o relatório chega sozinho por e-mail, com PDF e planilha.</div>
      {q.data ? <RecipientPicker value={q.data.map((r) => r.email)} onChange={(v) => save.mutate(v)} suggestions={suggestions} placeholder="Nome ou e-mail" /> : <Loading rows={1} height={48} />}
    </>
  );
}

// ───────── Acesso com login (convites e permissões) ─────────
function People({ tid, target, onChanged }: { tid: string; target: ShareTarget; onChanged: () => void }) {
  const toast = useToast();
  const key = ['shares', tid, target.type, target.id];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<ShareInfo>(`/api/t/${tid}/shares?type=${target.type}&id=${encodeURIComponent(target.id)}`) });
  const [email, setEmail] = useState('');
  const [perm, setPerm] = useState<Permission>('commenter');
  const add = useMutation({
    mutationFn: () => api.post<{ capped?: boolean; updated?: boolean }>(`/api/t/${tid}/shares`, { type: target.type, id: target.id, email: email.trim(), permission: perm }),
    onSuccess: (r) => { toast(r.capped ? 'Convite enviado como Comentador (clientes não podem editar)' : r.updated ? 'Permissão atualizada' : 'Convite enviado'); setEmail(''); onChanged(); q.refetch(); },
    onError: (e) => toast(errMsg(e), 'err'),
  });
  const setPermission = useMutation({
    mutationFn: ({ id, value }: { id: string; value: Permission | 'remove' }) =>
      value === 'remove' ? api.del(`/api/t/${tid}/shares/${id}`) : api.patch(`/api/t/${tid}/shares/${id}`, { permission: value }),
    onSuccess: () => { onChanged(); q.refetch(); },
    onError: (e) => toast(errMsg(e), 'err'),
  });
  const d = q.data;
  if (!d) return <Loading rows={3} height={44} />;
  return (
    <>
      <div className="small" style={{ marginBottom: 12, lineHeight: 1.5 }}>
        Quem recebe convite com login cria uma senha simples e acompanha tudo pelo Portal do cliente, inclusive <strong>respondendo às pendências</strong>.
      </div>
      {d.canManage && (
        <form onSubmit={(e) => { e.preventDefault(); if (!validEmail(email.trim())) return toast('Informe um e-mail válido', 'err'); add.mutate(); }} style={{ marginBottom: 16 }}>
          <input className="input sm" type="email" inputMode="email" placeholder="email@restaurante.com.br" value={email} onChange={(e) => setEmail(e.target.value)} style={{ marginBottom: 8 }} />
          <div className="row wrap gap-6">
            {PERM.map(([k, l]) => <button key={k} type="button" className={`chip toggle ${perm === k ? 'on' : ''}`} aria-pressed={perm === k} onClick={() => setPerm(k)}>{l}</button>)}
          </div>
          <div className="small" style={{ margin: '6px 0 10px' }}>{PERM_HINT[perm]}</div>
          <Button variant="accent" size="sm" block type="submit" loading={add.isPending} style={{ height: 44 }}>Enviar convite</Button>
        </form>
      )}
      <div className="label" style={{ marginBottom: 4 }}>Pessoas com acesso</div>
      {d.direct.length === 0 && d.inherited.length === 0 && <div className="small" style={{ padding: '10px 0' }}>Ninguém além da equipe.</div>}
      {d.direct.map((p) => (
        <div key={p.id} className="row gap-10" style={{ padding: '10px 0', borderBottom: '1px solid var(--divider-2)' }}>
          <Avatar name={p.nome} color={p.isCompany ? '#5F7386' : '#6F8C7C'} />
          <div className="flex1">
            <div className="wrapline" style={{ fontSize: 13.5, fontWeight: 500 }}>{p.nome}</div>
            <div className="wrapline" style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{p.email}{p.pending ? ' · ainda não entrou' : ''}</div>
          </div>
          {d.canManage ? (
            <select className="select xs" value={p.permission} aria-label={`Permissão de ${p.nome}`} onChange={(e) => setPermission.mutate({ id: p.id, value: e.target.value as Permission | 'remove' })}>
              {PERM.filter(([k]) => !(p.isCompany && k === 'editor')).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              <option value="remove">Remover</option>
            </select>
          ) : <span className="mono none small">{p.permLabel}</span>}
        </div>
      ))}
      {d.inherited.map((p, i) => (
        <div key={`${p.email}-${i}`} className="row gap-10" style={{ padding: '10px 0', borderBottom: '1px solid var(--divider-2)' }}>
          <Avatar name={p.nome} color={p.isCompany ? '#5F7386' : undefined} />
          <div className="flex1">
            <div className="wrapline" style={{ fontSize: 13.5, fontWeight: 500 }}>{p.nome}</div>
            <div className="small">{p.src}</div>
          </div>
          <span className="mono none small">{p.perm}</span>
        </div>
      ))}
    </>
  );
}

export function ShareSheet({ tid, target, onClose }: { tid: string; target: ShareTarget | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [view, setView] = useState<'send' | 'people'>('send');
  const sug = useQuery({
    queryKey: ['suggestions', tid, target?.type, target?.id],
    queryFn: () => api.get<Suggestion[]>(`/api/t/${tid}/shares/suggestions?type=${target!.type}&id=${encodeURIComponent(target!.id)}`),
    enabled: !!target,
  });
  const suggestions = useMemo(() => sug.data ?? [], [sug.data]);
  const close = () => { setView('send'); onClose(); };
  const isRest = target?.type === 'restaurant';
  const title = view === 'people' ? 'Acesso com login' : isRest ? 'Compartilhar restaurante' : 'Enviar relatório';

  return (
    <Sheet open={!!target} onClose={close} title={title} wide>
      {target && view === 'send' && (
        <>
          {isRest ? <AutoRecipients tid={tid} rid={target.id} suggestions={suggestions} /> : <SendReport tid={tid} target={target} suggestions={suggestions} />}
          <Divider>ou mande o link</Divider>
          <LinkButtons tid={tid} target={target} />
          <button type="button" className="row between" onClick={() => setView('people')} style={{ width: '100%', marginTop: 18, padding: '12px 0 0', border: 0, borderTop: '1px solid var(--divider)', background: 'none', textAlign: 'left' }}>
            <span>
              <span style={{ fontSize: 13.5, fontWeight: 500 }}>Quem tem acesso com login</span><br />
              <span className="small">Convidar o cliente para responder às pendências</span>
            </span>
            <span style={{ color: 'var(--accent)' }}>›</span>
          </button>
        </>
      )}
      {target && view === 'people' && (
        <>
          <button type="button" className="link-btn" style={{ marginBottom: 12, fontSize: 13 }} onClick={() => setView('send')}>← Voltar para enviar</button>
          <People tid={tid} target={target} onChanged={() => { qc.invalidateQueries({ queryKey: ['access', tid] }); sug.refetch(); }} />
        </>
      )}
    </Sheet>
  );
}
